import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';

/**
 * Wide-table wrapper that renders a horizontal scrollbar BOTH at the top
 * and at the bottom of the scroll region and keeps them in sync. Handy
 * for the class register (or any table wider than the viewport) so admin
 * doesn't have to scroll all the way down to reach the bar.
 *
 *   <DoubleScrollBox>
 *       <Table sx={{ minWidth: 1400 }}> ... </Table>
 *   </DoubleScrollBox>
 *
 * The top scrollbar is a thin div whose inner spacer matches the
 * content's scrollWidth. Both scroll containers sync via scrollLeft.
 * ResizeObserver + a MutationObserver on the content keep the spacer
 * width fresh when the underlying table changes size (row add / delete,
 * column widen, etc.).
 */
const DoubleScrollBox = ({ children, sx }) => {
    const topRef = useRef(null);
    const spacerRef = useRef(null);
    const bottomRef = useRef(null);
    const syncingRef = useRef(false);

    // Sync the spacer's width to the content's scrollWidth so the top
    // scrollbar knows how wide the scroll region actually is.
    useEffect(() => {
        const bottom = bottomRef.current;
        const spacer = spacerRef.current;
        if (!bottom || !spacer) return;
        const measure = () => {
            const w = bottom.scrollWidth;
            spacer.style.width = `${w}px`;
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(bottom);
        // Watch child mutations too — rows being added / removed change
        // scrollWidth without triggering a resize of the container.
        const mo = new MutationObserver(measure);
        mo.observe(bottom, { childList: true, subtree: true, characterData: true });
        return () => { ro.disconnect(); mo.disconnect(); };
    }, [children]);

    // Keep the two scrollbars in lockstep. The syncing flag avoids the
    // scroll → scroll ping-pong loop.
    useEffect(() => {
        const top = topRef.current;
        const bottom = bottomRef.current;
        if (!top || !bottom) return;
        const onTop = () => {
            if (syncingRef.current) return;
            syncingRef.current = true;
            bottom.scrollLeft = top.scrollLeft;
            requestAnimationFrame(() => { syncingRef.current = false; });
        };
        const onBottom = () => {
            if (syncingRef.current) return;
            syncingRef.current = true;
            top.scrollLeft = bottom.scrollLeft;
            requestAnimationFrame(() => { syncingRef.current = false; });
        };
        top.addEventListener('scroll', onTop);
        bottom.addEventListener('scroll', onBottom);
        return () => {
            top.removeEventListener('scroll', onTop);
            bottom.removeEventListener('scroll', onBottom);
        };
    }, []);

    return (
        <Box sx={sx}>
            <Box
                ref={topRef}
                sx={{
                    overflowX: 'auto',
                    // Small strip that's just tall enough to expose a native scrollbar.
                    height: 16,
                    // Hide vertical scroll on this row.
                    overflowY: 'hidden',
                }}
            >
                <Box ref={spacerRef} sx={{ height: 1, width: '100%' }} />
            </Box>
            <Box ref={bottomRef} sx={{ overflowX: 'auto' }}>
                {children}
            </Box>
        </Box>
    );
};

export default DoubleScrollBox;
